import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';
import {
  Typography,
  Stepper,
  Step,
  alpha,
  makeStyles,
} from '@material-ui/core';
import AttributeQuestion from '../../attributeQuestion';
import QuestionnaireQuestion from '../../questionnaireQuestion';
import ReviewFeedbackForm from '../../reviewFeedbackForm';
import TextBoxWithTooltip from '../../textBoxWithTooltip';
import RichTextPreview from '../../richTextPreview';
import CustomButton from '../../customButton';
import QuestionObserver from './questionObserver';
import {
  getItemById,
  isArrayEmpty,
  trimString,
} from '../../../../utility/helpers';
import { sticky } from '../../../../constants/helperCssRules';
import { isRichTextEditorEmpty } from '../../../../utility/validation';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    borderBottom: `1px dashed ${primary.bluish1}`,
    paddingBottom: spacing(6),
    position: 'relative',
  },
  headerWrapper: {
    backgroundColor: primary.white,
    paddingTop: spacing(8),
    paddingBottom: spacing(4),
    transition: 'box-shadow 0.3s ease',
  },
  stickyHeader: {
    boxShadow: `0px 4px 4px -2px ${alpha(primary.black, 0.12)}`,
    ...sticky(primary.white, 105, 998),
    transition: 'box-shadow 0.3s ease',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: spacing(2),
  },
  title: {
    color: primary.bluish3,
  },
  stepperWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  stepper: { flexGrow: 1 },
  step: {
    backgroundColor: primary.bluish7,
    borderRadius: 30,
    flexGrow: 1,
    flexShrink: 0,
    height: 8,
    padding: 0,
    marginRight: spacing(1.5),
  },
  completedStep: { backgroundColor: primary.bluish1 },
  stepperLabelWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    minWidth: 25,
  },
  stepperLabel: {
    color: primary.bluish3,
    fontSize: 12,
    lineHeight: '14px',
  },
  description: { marginTop: spacing(6) },
  reviewContent: { marginTop: spacing(6) },
  totalReviews: {
    color: primary.bluish3,
    marginLeft: spacing(2),
    flexShrink: 0,
    transition: 'color 0.3s ease',
  },
  questionWrapper: {
    marginBottom: spacing(6),
    '&:last-of-type': { marginBottom: 0 },
  },
  question: { paddingRight: 0 },
  commentButtonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: spacing(4),
  },
  button: {
    padding: spacing(2, 4),
    '& span': {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 16,
      lineHeight: '24px',
      textTransform: 'none',
    },
  },
  commentButton: {
    color: primary.bluish1,
    '&:disabled': {
      border: `1px solid ${primary.bluish7}`,
      backgroundColor: primary.white,
      '& span': { color: primary.bluish7, '& img': { opacity: 0.2 } },
    },
  },
  feedback: { marginTop: spacing(6) },
  feedbackField: {
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  bottomSentinel: {
    position: 'absolute',
    left: 0,
    bottom: 24,
    width: '100%',
    height: 1,
  },
}));

const Review = ({
  translations,
  survey,
  review,
  reviewIndex,
  isQuestionnaire,
  selectedSubject = null,
  onViewComment,
  onLoadNextReview,
}) => {
  const classes = useStyles();
  const { surveyTitle, isSingleSurvey, totalReviews } = survey;
  const {
    name,
    visible_description,
    description,
    questions,
    results,
    comments,
    subjects,
    isVertical,
    isEnps,
    hasNeutralAnswer,
    withTextReplyQuestions,
    hasAnswerComment,
    hasAdditionalFeedback,
    isSubmitted,
  } = review;
  const [subject] = subjects;
  const hasFeedback =
    hasAdditionalFeedback && comments.some(c => !!trimString(c.comment));

  const { ref, inView: isReviewInView } = useInView({
    threshold: 0,
    rootMargin: '-105px 0px 0px 0px',
  });

  const { ref: reviewBottomRef, inView: bottomInView } = useInView({
    triggerOnce: true,
    threshold: 0,
    rootMargin: '-105px 0px 0px 0px',
  });

  const [activeStep, setActiveStep] = useState(-1);

  useEffect(() => {
    if (bottomInView) {
      onLoadNextReview(review.id);
    }
  }, [bottomInView, review, onLoadNextReview]);

  return (
    <div ref={ref} className={classes.root}>
      <div
        className={classNames(classes.headerWrapper, {
          [classes.stickyHeader]: isReviewInView,
        })}
      >
        <div className={classes.titleWrapper}>
          <TextBoxWithTooltip
            className={classNames(classes.title)}
            variant="h4"
            text={isEnps ? surveyTitle : name}
          />
          <Typography variant="subtitle2" className={classes.totalReviews}>
            {`(${reviewIndex + 1}/${totalReviews})`}
          </Typography>
        </div>
        <div className={classes.stepperWrapper}>
          <Stepper
            classes={{ root: classes.stepper }}
            activeStep={activeStep}
            connector={null}
          >
            {questions.map((qStep, qIndex) => (
              <Step
                key={qStep.id}
                classes={{
                  horizontal: classes.step,
                  completed: classes.completedStep,
                }}
                completed={activeStep >= qIndex}
              />
            ))}
          </Stepper>
          <div className={classes.stepperLabelWrapper}>
            <Typography className={classes.stepperLabel} variant="caption">{`${
              activeStep + 1
            }/ `}</Typography>
            <Typography className={classes.stepperLabel} variant="caption">
              {questions.length}
            </Typography>
          </div>
        </div>
      </div>

      {!isRichTextEditorEmpty(visible_description) ||
        (isQuestionnaire && !isRichTextEditorEmpty(description) && (
          <RichTextPreview
            className={classes.description}
            text={isQuestionnaire ? description : visible_description}
          />
        ))}

      <div className={classes.reviewContent}>
        {questions.map((question, questionIndex) => {
          const questionComment = isSingleSurvey
            ? subjects?.[0]?.replied?.[question?.id]
            : getItemById(subjects, selectedSubject?.id)?.replied?.[
                question?.id
              ];
          const isEnpsOpenEndedQuestion =
            isEnps && isArrayEmpty(question.answers);
          const questionWithTextReply =
            question.text_reply_questions ?? withTextReplyQuestions;
          return (
            <QuestionObserver
              key={`question_${question.id}`}
              className={classes.questionWrapper}
              questionIndex={questionIndex}
              onActiveChange={setActiveStep}
            >
              <div>
                {isQuestionnaire ? (
                  <QuestionnaireQuestion
                    scrollClass={classes.question}
                    translations={translations.answers}
                    question={question}
                    subjects={subjects}
                    results={results}
                    isSingleReview={isSingleSurvey}
                    isPreview={isSubmitted}
                    isHistoryView
                  />
                ) : (
                  <AttributeQuestion
                    scrollClass={classes.question}
                    translations={translations.answers}
                    isVertical={isVertical}
                    question={question}
                    results={results}
                    isEnpsOpenEndedQuestion={isEnpsOpenEndedQuestion}
                    isSingleReview={isSingleSurvey}
                    hasNeutralAnswer={hasNeutralAnswer}
                    withTextReplyQuestions={questionWithTextReply}
                    hasAddCommentButton={hasAnswerComment}
                    isPreview={isSubmitted}
                    subjects={subjects}
                    onAddComment={onViewComment}
                    isInsightsView
                  />
                )}
                {!isQuestionnaire &&
                  !questionWithTextReply &&
                  isSingleSurvey &&
                  hasAnswerComment &&
                  !!questionComment?.reply && (
                    <div className={classes.commentButtonWrapper}>
                      <CustomButton
                        className={classNames(
                          classes.button,
                          classes.commentButton
                        )}
                        type="editCommentDarkRoundedOutlined"
                        onClick={() => onViewComment(subject, question.id)}
                      >
                        {translations.addCommentForm.title.view}
                      </CustomButton>
                    </div>
                  )}
              </div>
            </QuestionObserver>
          );
        })}

        {hasFeedback && (
          <ReviewFeedbackForm
            className={classes.feedback}
            fieldWrapperClass={classes.feedbackField}
            translations={translations.feedbackForm}
            initialComments={comments}
            subjects={subjects}
            isSingleReview={isSingleSurvey}
            isPreview={isSubmitted}
          />
        )}
      </div>
      <div ref={reviewBottomRef} className={classes.bottomSentinel} />
    </div>
  );
};

Review.propTypes = {
  translations: PropTypes.object.isRequired,
  survey: PropTypes.object.isRequired,
  review: PropTypes.object.isRequired,
  reviewIndex: PropTypes.number.isRequired,
  selectedSubject: PropTypes.object,
  isQuestionnaire: PropTypes.bool.isRequired,
  onViewComment: PropTypes.func.isRequired,
  onLoadNextReview: PropTypes.func.isRequired,
};

export default Review;
